import { LocalDateTime } from '../localDate/DateTimeView';
import ICoordinate from '../../features/map/ICoordinate';

interface IIdentity {
    id: number;
}

export interface IOrderListItem extends IIdentity {
    orderCollectionNumber: string;
    brokerAgentId: number;
    orderNumber: string;
    orderPlacedAt: any;
    orderCollectionId: number;
    address: string;
    city?: string;
    status: IStatus;
    site: number;
    orderType: IOrderType;
    productProperties: any[];
    isExpress: boolean;
    isExpressWeekend: boolean;
    isEarlyDelivery: boolean;
    isArchived: boolean;
    archivedStatus: number;
    legacyOrderNumber: string;
    photographer: string;
    packageProduct: string;
    bookingStart: Date;
    customerDeliveryAnnotation: boolean;
}

export interface IUnBookedOrderListItem {
    orderId: number;
    orderNumber: number;
    orderCollectionNumber: number;
    orderCollectionId: number;
    createdAt: LocalDateTime;
    brokerFirmName: string;
    packageProduct: string;
    city: string;
    address: string;
    preferredDate: LocalDateTime;
    preferredTimeSlot: string;
    regionId: string;
    assignedUserId: number;
    assignedUserName: string;
    isRead: boolean;
    productProperties: any[];
    isExpress: string;
    isExpressWeekend: string;
    isEarlyDelivery: string;
    site: number;
}

export interface IEnumeration {
    value: number;
    displayName: string;
}

export interface IOrderType extends IEnumeration {
    translateKey: string;
}

export interface IInvoiceStatus {
    value: number;
    displayName: string;
}

export interface ICurrency {
    prefix: string;
    suffix: string;
}

export interface IStatus {
    value: number;
    displayName: string;
}

export interface IOrderProductInfo {
    selectedProduct: number | null;
    selectedAdditionalProducts: number[];
    selectedOrderAdditions: number[];
}

export interface IBrokerFirm extends IIdentity {
    isHeadQuarter: boolean;
    currency: ICurrency;
}

export interface IBrokerAgent extends IIdentity {
    availableBrokerFirms: IBrokerFirm[];
}

export interface ICreateOrder extends IIdentity {
    invoiceReceiver: IInvoiceReceiver;
    address: ICreateOrderAddress;
    contact: ICreateOrderContact;
    other: ICreateOrderOther;
    products: any;
    agreementAccepted: boolean;
    availableBrokerAgents: IBrokerAgent[];
    onlyValidate: boolean;
    pendingOrderId?: string;
    existingPlanOrderId?: number;
    objectTypes: IEnumeration[];
    productInfo: IOrderProductInfo;
    contentProviderId?: number;
    bookDirectly: boolean;
    isBookingRequest?: boolean;
}

export interface ICreateOrderOther {
    willOrderCreatorAttendWhenPhotographing: boolean;
    preferredTimeOfPhotographing: any;
    preferredTimeOfPhotographingSlot: string;
    notes: string;
    extraDeliveryRecipients: string;
    contactPerson: number;
    keyPickupAddress?: string;
    keyPickupZip?: string;
    keyPickupCity?: string;
}

export interface ICreateOrderContact {
    ownerFirstName: string;
    ownerLastName: string;
    ownerSecondaryPhoneNumber: string;
    ownerPrimaryPhoneNumber: string;
    ownerEmailAddress: string;
}

export interface ICreateOrderAddress {
    addressLine1: string;
    addressCity: string;
    addressPostalNumber: string;
    addressDistrict: string;
    addressPointLocation: ICoordinate;
    objectAreaSquareMeters: string;
    objectNumberOfRooms: string;
    objectType: number;
    objectYearOfConstruction: string;
    apartmentHasBalcony: boolean;
    apartmentFloor: string;
    apartmentAccessCode: string;
}

export interface IInvoiceReceiver {
    projectNumber: string;
    receiver: number;
    otherBrokerFirm: number;
    invoiceBillingMethod: number;
    allowsSeparateBillingAddress: boolean;
    separateBillingAddress: boolean;
    organizationNumber: string;
    legalName: string;
    reference: string;

    addressLine1: string;
    postalNumber: number;
    city: string;

    sellerName: string;
    sellerEmail: string;
    sellerPersonalNumber: string;
    sellerAgreementAccepted: boolean;
}

export interface INamedIdentity extends IIdentity {
    name: string;
}

export interface IPhotoOrderListItem {
    orderId: number;
    orderNumber: string;
    orderCollectionNumber: string;
    endTime: any;
    orderStatus: number;
    orderCollectionId: number;
    performedAt: Date;
    brokerFirmName: string;
    photographerName: string;
    packageProduct: string;
    orderLockedBy: string;
    imagesExists: string;
    numberOfImages: number;
    isExpress: string;
    isExpressWeekend: string;
    isFilm: string;
    isText: boolean;
    isVisualization: boolean;
    isEarlyDelivery: string;
    orderStatusTranslation: string;
    regionId: number;
    imageEditorFirmId: number;
    assignedUserName: string;
    notes: string;
    productProperties: any[];
    site: number;
    orderStatuses: any[];
    delivered: boolean;
    completedTime: any;
    deadline: any;
    isOldEditOrder: boolean;
}

export interface IFilmOrderListItem {
    orderId: number;
    orderNumber: number;
    orderCollectionNumber: number;
    address: string;
    orderCollectionId: number;
    filmStatus: IEnumeration;
    photographerName: string;
    createdAt: LocalDateTime;
    deadline: LocalDateTime;
    brokerFirmName: string;
    productName: string;
    city: string;
    district: string;
    preferredDate: LocalDateTime;
    regionId: string;
    assignedUserName: string;
    isRead: boolean;
    productProperties: any[];
    // True when the order holds at least one film product of that type. Both can be
    // true at once: one order can contain a regular film and a SoMe film.
    hasRegularFilm: boolean;
    hasSoMeFilm: boolean;
    isExpress: string;
    isExpressWeekend: string;
    isEarlyDelivery: string;
    site: number;
}

export enum ProductProperties {
    Matterport = 64,
    Video = 19,
    Measuring = 27,
    Text = 62,
    Drone = 65
}

export enum InvoiceSellerStatus {
    Unknown = 0,
    Show = 1,
    Hidden = 2,
    Mandatory = 3
}

export enum InvoiceBillingMethods {
    Unknown = 0,
    Email = 1,
    Mail = 2
}

export enum InvoiceReceiverTypes {
    Unknown = 0,
    Broker = 1,
    Company = 2,
    Seller = 3,
    OtherCompany = 4
}

export enum CustomerTypes {
    Unknown = 0,
    Private = 1,
    Commercial = 2
}
